import React from 'react';
import { Box, Tooltip, Button, Snackbar, Alert, Chip } from '@mui/material';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import AddIcon from '@mui/icons-material/Add';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import LoadingSpinner from '../../components/LoadingSpinner';
import { NoContent, ReactTable, Modal } from '../../components/shared';
import SearchBar from '../../components/SearchBar';
import NewFilterDrawer from '../../components/shared/FilterDrawer/NewFilterDrawer';
import ConfirmationModal from '../../components/ConfirmationModel/ConfirmationModal';
import EditAreaTaxLocationModal from './EditAreaTaxLocationModal';
import SalesTaxHistoryModal from '../Location/SubLocationDetails/SalesTaxHistoryModal';
import { useAreaTaxLocationsTab } from './hooks';

const AreaTaxLocationsTab = ({ status, isActiveTab, onTotalCount }) => {
  const {
    tabFilters,
    snackbar,
    isModalOpen,
    selectedRowData,
    showAreaTaxHistoryModal,
    selectedAreaTaxLocation,
    stateObj,
    expandedRows,
    showStatusModal,
    locationToToggle,

    tableData,
    totalCount,
    isLoading,
    isError,
    isFetching,
    columns,
    filterParams,
    filterData,

    DRAWER_TYPES,
    TITLE,
    FILTER_DATA,

    toggleExpandRow,
    handleEditClick,
    handleCloseModal,
    handleConfirmModal,
    handleToggleStatus,
    handleConfirmStatusToggle,
    handleCancelStatusToggle,
    handleOpenHistoryModal,
    handleCloseHistoryModal,
    pageChangeHandler,
    perPageChangeHandler,
    searchHandler,
    onSearchReset,
    applyFilterHandler,
    resetFilterParams,
    navigateToAddNew,
    closeSnackbar,
  } = useAreaTaxLocationsTab({ status, isActiveTab, onTotalCount });

  const tableColumns = React.useMemo(() => {
    if (!columns || !Array.isArray(columns)) return [];

    const updatedColumns = columns.map((col) => {
      if (col.accessor === 'sublocations') {
        return {
          ...col,
          Cell: ({ row }) => {
            const { sublocations } = row.original;
            const activeSublocations = sublocations?.active || [];
            const archivedSublocations = sublocations?.archived || [];
            const [expanded, setExpanded] = React.useState(false);

            const totalCount = activeSublocations.length + archivedSublocations.length;

            if (totalCount === 0) {
              return 'N/A';
            }

            let displayActive = activeSublocations;
            let displayArchived = archivedSublocations;

            if (!expanded && totalCount > 2) {
              if (activeSublocations.length === 0) {
                displayArchived = archivedSublocations.slice(0, 2);
              } else if (archivedSublocations.length === 0) {
                displayActive = activeSublocations.slice(0, 2);
              } else {
                displayActive = activeSublocations.slice(0, 1);
                displayArchived = archivedSublocations.slice(0, 1);
              }
            }

            const hiddenCount = totalCount - (displayActive.length + displayArchived.length);

            return (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
              >
                {displayActive.map((sub) => (
                  <Chip
                    key={sub.id}
                    label={sub.name}
                    size="small"
                    variant="outlined"
                    color="success"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
                {displayArchived.map((sub) => (
                  <Chip
                    key={sub.id}
                    label={sub.name}
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
                {!expanded && hiddenCount > 0 && (
                  <Chip
                    label={`+${hiddenCount}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.7rem',
                      height: '20px',
                      fontWeight: 600,
                      borderColor: '#999',
                      color: '#666',
                    }}
                  />
                )}
              </Box>
            );
          },
        };
      }
      return col;
    });

    const actionColumn = {
      Header: () => null,
      id: 'editLocation',
      width: 220,
      Cell: ({ row }) => {
        const activeSublocations = row?.original?.sublocations?.active || [];
        const hasSublocations = activeSublocations.length > 0;

        const isActive = row?.original?.status;

        return (
          <Box display="flex" gap={5} alignItems="center" justifyContent="center">
            <Tooltip title="Edit" arrow>
              <span data-testid="editIcon">
                <ModeEditIcon onClick={() => handleEditClick(row?.original)} style={{ cursor: 'pointer' }} />
              </span>
            </Tooltip>
            <Tooltip
              title={
                isActive && hasSublocations
                  ? 'Please remove the association before deactivating the area tax location'
                  : isActive
                  ? 'Deactivate'
                  : 'Activate'
              }
              arrow
            >
              <span data-testid="toggleStatusIcon">
                {isActive ? (
                  <ToggleOnIcon
                    onClick={() => !hasSublocations && handleToggleStatus(row?.original)}
                    style={{
                      cursor: hasSublocations ? 'not-allowed' : 'pointer',
                      opacity: hasSublocations ? 0.5 : 1,
                      color: hasSublocations ? 'gray' : 'green',
                      fontSize: '2.5rem',
                    }}
                  />
                ) : (
                  <ToggleOffIcon
                    onClick={() => handleToggleStatus(row?.original)}
                    style={{
                      cursor: 'pointer',
                      color: 'gray',
                      fontSize: '2.5rem',
                    }}
                  />
                )}
              </span>
            </Tooltip>
            <Tooltip title="Add Rate" arrow>
              <span data-testid="historyIcon">
                <AddIcon onClick={() => handleOpenHistoryModal(row?.original)} style={{ cursor: 'pointer' }} />
              </span>
            </Tooltip>
          </Box>
        );
      },
    };

    return [...updatedColumns, actionColumn];
  }, [columns, expandedRows, toggleExpandRow, handleEditClick, handleToggleStatus, handleOpenHistoryModal]);

  return (
    <>
      {/* Search and Filter Bar */}
      <Box display="flex" marginBottom={1} padding={2} gap={2} alignItems="center">
        <Box width="100%">
          <SearchBar
            onSearch={searchHandler}
            placeholder="Search by location name"
            onReset={onSearchReset}
            searchText={tabFilters?.searchText || ''}
          />
        </Box>
        <Box pl={3}>
          <NewFilterDrawer
            filteredValues={filterParams || {}}
            name={TITLE?.FILTERS?.toUpperCase() || 'FILTERS'}
            title={TITLE?.FILTERS || 'Filters'}
            drawerType={DRAWER_TYPES?.FILTER_DRAWER}
            onReset={resetFilterParams}
            onApplyFilter={applyFilterHandler}
            filterData={filterData || []}
            emptyFilterLength={JSON.stringify(FILTER_DATA?.MANAGE_AREA_TAX_LOCATION?.INITIAL_PARAMS || {}).length}
          />
        </Box>
        <Box pl={3}>
          <Button
            variant="contained"
            color="primary"
            sx={{ height: '100%', whiteSpace: 'nowrap' }}
            onClick={navigateToAddNew}
          >
            +ADD NEW
          </Button>
        </Box>
      </Box>

      {/* Table Content */}
      <Box>
        {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
        {isError && <NoContent title="No Area Tax Locations Found" desc={<>No Data Found</>} />}

        {!isLoading && !isFetching && !isError && tableData && tableData.length > 0 && (
          <ReactTable
            data={tableData}
            columns={tableColumns}
            pagination
            manualPagination
            totalCount={totalCount || 0}
            searchParams=""
            pageNumberForTablePagination={(tabFilters?.pageNumber || 1) - 1}
            onPageChange={pageChangeHandler}
            onPerPageChange={perPageChangeHandler}
            perPage={tabFilters?.rowsPerPage || 10}
          />
        )}
      </Box>

      {/* Edit Modal */}
      {isModalOpen && (
        <EditAreaTaxLocationModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmModal}
          selectedLocation={selectedRowData}
        />
      )}

      {/* Status Toggle Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStatusModal || false}
        title={`${locationToToggle?.status ? 'Deactivate' : 'Activate'} Area Tax Location`}
        msg={`Are you sure you want to ${locationToToggle?.status ? 'deactivate' : 'activate'} "${
          locationToToggle?.areaTaxLocationName || ''
        }"? ${
          locationToToggle?.status
            ? 'This location will no longer be available for selection.'
            : 'This location will become available for selection again.'
        }`}
        buttons={[
          { text: locationToToggle?.status ? 'Deactivate' : 'Activate', value: true },
          { text: 'Cancel', value: false },
        ]}
        onClick={(value) => {
          if (value) handleConfirmStatusToggle();
          else handleCancelStatusToggle();
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar?.open || false}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar?.severity || 'success'} sx={{ width: '100%' }}>
          {snackbar?.message || ''}
        </Alert>
      </Snackbar>

      {/* Area Tax History Modal */}
      <Modal
        open={showAreaTaxHistoryModal}
        onClose={handleCloseHistoryModal}
        title={
          selectedAreaTaxLocation
            ? `Area Tax History for ${selectedAreaTaxLocation?.areaTaxLocationName}`
            : 'Area Tax History'
        }
        titleVariant="subtitle10"
        closeIcon={true}
        PaperProps={{
          sx: {
            width: '75%',
            maxWidth: 'none',
            maxHeight: '60%',
            height: '70%',
            overflow: 'hidden',
          },
        }}
      >
        {selectedAreaTaxLocation && (
          <SalesTaxHistoryModal
            sublocation={{
              id: selectedAreaTaxLocation.id,
              areaTaxLocations: {
                id: selectedAreaTaxLocation.id,
                areaTaxLocationName: selectedAreaTaxLocation.areaTaxLocationName,
              },
              areaTaxLocationId: selectedAreaTaxLocation.id,
            }}
            taxModal="Area"
            stateIdObj={stateObj}
            validAreaTaxLocationId={true}
          />
        )}
      </Modal>
    </>
  );
};

export default AreaTaxLocationsTab;
